import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { OffersTable } from "../../../components/dashboard/offers/offers-table";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getOffersAsync, initialController } from "../../../slices/offer";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PlusIcon } from "@heroicons/react/24/outline";
import { updateUrlWithException } from "../../../components/Utils";
import { debounce } from "lodash";
import {
  orderStatusOptions,
  ordersViewsOptions,
} from "../../../components/dashboard/orders/utils";
import { useSearchParams } from "react-router-dom";

export const OffersList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();

  const [controller, setController] = useState({
    ...initialController,
    filters: searchParams.get("filters") ? searchParams.get("filters") : null,
    page: searchParams.get("page") ? parseInt(searchParams.get("page")) : 1,
    per_page: searchParams.get("per_page") ? parseInt(searchParams.get("per_page")) : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "DESC",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "offer_number",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    fields: "id,offer_number,offer_date,customer,project,status,total_amount",
  });

  const itemState = useSelector((store) => {
    return store.offers;
  });

  // Update all params from controller except filters (dont't touch filters)
  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      // This will be preserved
      "filters"
    );
  }, [controller]);

  const { isLoading, error, errors } = itemState;
  const [selectedItems, setSelectedItems] = useState([]);

  const handleFiltersChange = (filters) => {
    // Convert filters to controller format
    const filterArray = [];
    
    if (filters.statuses && filters.statuses.length > 0) {
      filterArray.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }
    
    if (filters.view) {
      filterArray.push({
        name: "view",
        value: filters.view,
      });
    }
    
    setController((prev) => ({
      ...prev,
      page: 1,
      query: filters.query || "",
      filters: filterArray.length > 0 ? filterArray : null,
    }));
  };

  const handlePageChange = (newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const debouncedDispatchAction = debounce((controller) => {
    dispatch(getOffersAsync(controller));
  }, 250);

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const headerActions = [];

  // Build initial filters for ModuleFilter
  const getStatusFilters = () => {
    if (!controller.filters) return [];
    if (typeof controller.filters === 'string') {
      try {
        const parsed = JSON.parse(controller.filters);
        if (Array.isArray(parsed)) {
          const statusFilter = parsed.find((f) => f.name === "status");
          return statusFilter?.value ? JSON.parse(statusFilter.value) : [];
        }
      } catch (e) {
        return [];
      }
    }
    if (Array.isArray(controller.filters)) {
      const statusFilter = controller.filters.find((f) => f.name === "status");
      return statusFilter?.value ? JSON.parse(statusFilter.value) : [];
    }
    return [];
  };

  const getViewFilter = () => {
    if (!controller.filters) return controller.view || "all";
    if (typeof controller.filters === 'string') {
      try {
        const parsed = JSON.parse(controller.filters);
        if (Array.isArray(parsed)) {
          const viewFilter = parsed.find((f) => f.name === "view");
          return viewFilter?.value || controller.view || "all";
        }
      } catch (e) {
        return controller.view || "all";
      }
    }
    if (Array.isArray(controller.filters)) {
      const viewFilter = controller.filters.find((f) => f.name === "view");
      return viewFilter?.value || controller.view || "all";
    }
    return controller.view || "all";
  };

  const getViewFilterArray = () => {
    const viewValue = getViewFilter();
    // ModuleFilter expects view as an array for custom dropdown filters
    return viewValue ? [viewValue] : [];
  };

  const initialFilters = {
    query: controller.query || "",
    statuses: getStatusFilters(),
    view: getViewFilterArray(),
  };

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={"Tilbud"}
        meta={[]}
        mainButton={{
          label: "Nytt",
          action: null,
          path: "/dashboard/offers/new",
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={initialFilters}
              customDropdownFilters={[
                {
                  key: "view",
                  label: t("View"),
                  options: ordersViewsOptions,
                },
                {
                  key: "status",
                  label: t("Status"),
                  options: orderStatusOptions,
                },
              ]}
            />

            <OffersTable
              itemState={itemState}
              itemsCount={itemState.totalCount}
              currentCount={itemState.currentCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
            />
          </div>
        </div>
      </DashboardHeader>
    </>
  );
};
